import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency } from '../../lib/formatters';
import { tokenStorage } from '../../lib/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SortableHeader = ({ children, column, currentSort, onSort, className = '' }) => (
  <TableHead
    className={`cursor-pointer hover:bg-gray-200 ${className}`}
    onClick={() => onSort(column)}
  >
    {children} {currentSort.column === column && (currentSort.direction === 'asc' ? '↑' : '↓')}
  </TableHead>
);

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'Funded':
      return 'bg-green-100 text-green-800';
    case 'Paid in Full':
    case 'Approved':
      return 'bg-blue-100 text-blue-800';
    case 'Payment Issues':
    case 'In-House Collection':
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'Default':
    case 'Rejected':
      return 'bg-red-100 text-red-800';
    case 'Undone':
      return 'bg-gray-100 text-gray-500 line-through';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getAuthHeaders = () => {
  const token = tokenStorage.getToken();
  return { Authorization: `Bearer ${token}` };
};

const WithdrawalHistoryTable = ({
  withdrawals,
  deals = [],
  sortConfig,
  onSort,
  onExportCsv,
  onRefresh,
  isAdmin = false
}) => {
  const [undoingId, setUndoingId] = useState(null);

  const handleUndo = async (withdrawal) => {
    if (!window.confirm(`Are you sure you want to undo this withdrawal of ${formatCurrency(withdrawal.amount)}? The money will be returned to the syndicator's balance.`)) {
      return;
    }

    setUndoingId(withdrawal.id);
    try {
      await axios.post(
        `${API_URL}/api/withdrawals/${withdrawal.id}/undo`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Withdrawal of ${formatCurrency(withdrawal.amount)} has been undone`);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error undoing withdrawal:', error);
      toast.error(error.response?.data?.detail || 'Failed to undo withdrawal');
    } finally {
      setUndoingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Withdrawal History</h2>
          <p className="text-xs text-gray-500">
            {withdrawals.length} withdrawal records found
          </p>
        </div>
        <div className="flex gap-2">
          {onRefresh && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
            >
              Refresh
            </Button>
          )}
          {onExportCsv && (
            <Button
              size="sm"
              variant="outline"
              onClick={onExportCsv}
              disabled={withdrawals.length === 0}
            >
              Export CSV
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-100">
              <SortableHeader column="request_date" currentSort={sortConfig} onSort={onSort}>
                Date
              </SortableHeader>
              <SortableHeader column="deal_code" currentSort={sortConfig} onSort={onSort}>
                Deal Code
              </SortableHeader>
              <SortableHeader column="amount" currentSort={sortConfig} onSort={onSort} className="text-right">
                Withdrawal Amount
              </SortableHeader>
              <TableHead className="text-right">Initial Investment</TableHead>
              <TableHead className="text-right">Expected Return</TableHead>
              <TableHead className="text-right">Collected So Far</TableHead>
              <TableHead className="text-right">Total Withdrawn</TableHead>
              <TableHead className="text-right">Available Balance</TableHead>
              <TableHead className="text-center">Status</TableHead>
              {isAdmin && <TableHead className="text-center">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {withdrawals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-6 text-gray-500">
                  No withdrawals recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              withdrawals.map((w) => {
                const synDeal = deals?.find(d => d.deal_id === w.deal_id) || {};
                const initialInvestment = synDeal?.advance_amount || 0;
                const expectedReturn = synDeal?.target_collection || 0;
                const collectedSoFar = synDeal?.running_collection || 0;
                const totalWithdrawn = synDeal?.withdrawals || 0;
                const availableBalance = synDeal?.balance || 0;
                
                const displayDate = w.request_date ? new Date(w.request_date).toLocaleDateString() : '-';
                const displayDeal = w.deal_code || w.deal_name || 'All Deals';
                const isUndone = w.status === 'Undone';

                return (
                  <TableRow 
                    key={w.id} 
                    className={`hover:bg-gray-50 ${isUndone ? 'opacity-50 bg-gray-50' : ''}`}
                  >
                    <TableCell className={isUndone ? 'line-through' : ''}>{displayDate}</TableCell>
                    <TableCell className={`text-blue-600 font-medium ${isUndone ? 'line-through' : ''}`}>
                      {displayDeal}
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${isUndone ? 'line-through' : ''}`}>
                      {formatCurrency(w.amount)}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(initialInvestment)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(expectedReturn)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(collectedSoFar)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(totalWithdrawn)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(availableBalance)}</TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(w.status)}`}>
                        {w.status || 'Completed'}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-center">
                        {!isUndone ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUndo(w)}
                            disabled={undoingId === w.id}
                            className="text-orange-600 border-orange-600 hover:bg-orange-50"
                            data-testid={`undo-btn-${w.id}`}
                          >
                            {undoingId === w.id ? 'Undoing...' : 'Undo'}
                          </Button>
                        ) : (
                          <span className="text-gray-400 text-xs">Reversed</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default WithdrawalHistoryTable;
